import { cn } from '@gettactic/helpers';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { DropdownMenuContentVariants } from '@v2/components/ui/dropdown-menu';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
            'select_trigger flex h-10 min-h-[42px] w-full cursor-pointer items-center justify-start gap-1 rounded-md bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className
        )}
        {...props}
    >
        {children}
        <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 opacity-50" />
        </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectClearTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
            'mr-1 flex h-10 min-h-[42px] w-full cursor-pointer items-center justify-between bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            className
        )}
        {...props}
    >
        {children}
        <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4" />
        </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
));
SelectClearTrigger.displayName = 'SelectClearTrigger';

const SelectContentVariants = {
    default: '',
    dropdown: DropdownMenuContentVariants.dropdown
};

export const SelectContentPositionClassNames = {
    borderDividerBottom: 'border-neutral-100 border-b',
    borderDividerTop: 'border-neutral-100 border-t',
}
type SelectContentProps = React.ComponentPropsWithoutRef<
    typeof SelectPrimitive.Content
> & {
    TopElement?: React.ReactNode;
    BottomElement?: React.ReactNode;
    scrollViewportClassname?: string;
    variant?: keyof typeof SelectContentVariants;
};
const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    SelectContentProps
>(
    (
        {
            className,
            children,
            position = 'popper',
            TopElement,
            BottomElement,
            scrollViewportClassname,
            variant = 'default',
            ...props
        },
        ref
    ) => (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                ref={ref}
                className={cn(
                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-[15.3rem] overflow-hidden rounded-border bg-popover p-0 text-popover-foreground data-[state=closed]:animate-out data-[state=open]:animate-in',
                    SelectContentVariants[variant],
                    position === 'popper' &&
                    'data-[side=left]:-translate-x-1 data-[side=top]:-translate-y-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1',
                    className
                )}
                position={position}
                {...props}
            >
                {!!TopElement && <div className={cn(SelectContentPositionClassNames.borderDividerBottom)}>{TopElement}</div>}
                <ScrollArea.Root type="auto">
                    <SelectPrimitive.Viewport
                        asChild
                        className={cn(
                            position === 'popper' &&
                            'mr-3 w-full min-w-[var(--radix-select-trigger-width)]'
                        )}
                    >
                        <ScrollArea.Viewport className={cn('max-h-60 min-h-[var(--radix-select-trigger-height)] p-4 py-2', scrollViewportClassname)}
                            // to prevent Updating a style property during rerender (overflowY) 
                            style={{ overflowY: undefined }}
                        >
                            {children}
                        </ScrollArea.Viewport>
                    </SelectPrimitive.Viewport>
                    <ScrollArea.Scrollbar
                        orientation="vertical"
                        className="flex w-2 p-[2px] hover:bg-gray-50"
                    >
                        <ScrollArea.Thumb className="w-full cursor-pointer rounded-full bg-gray-200" />
                    </ScrollArea.Scrollbar>
                </ScrollArea.Root>
                {!!BottomElement && (
                    <div className={cn(SelectContentPositionClassNames.borderDividerTop, 'py-1')}>{BottomElement}</div>
                )}
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    )
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label
        ref={ref}
        className={cn('py-1.5 pr-2 pl-8 font-semibold text-sm', className)}
        {...props}
    />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={cn('my-1 h-px bg-muted', className)}
        {...props}
    />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
    Select,
    SelectGroup,
    SelectValue,
    SelectTrigger,
    SelectContent,
    SelectLabel,
    SelectSeparator,
    SelectClearTrigger
};
